import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Phone, Video, MoreVertical, Send, 
  Image as ImageIcon, X, Mic, Paperclip, Reply, Forward // 👈 "File" hier entfernt!
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  getChatName, getChatAvatar, isUserOnline, getInitials, truncateText 
} from '@/lib/utils';
import wsService from '@/lib/websocket';
import { uploadAPI, messageAPI } from '@/lib/api'; 
import api from '@/lib/api'; 
import { toast } from 'sonner';
import VoiceRecorder from '@/components/VoiceRecorder';
import MessageBubble from '@/components/MessageBubble';
import GroupInfoModal from '@/components/GroupInfoModal';

export const ChatView = ({ 
  chat, 
  messages, 
  user, 
  onSendMessage, 
  onBack, 
  isMobile, 
  typingUserId, 
  onStartCall, 
  onMessagesUpdate,
  allChats,
  onChatUpdate 
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [forwardMessage, setForwardMessage] = useState(null);
  const [forwardingTo, setForwardingTo] = useState(null);
  
  const [isGroupInfoOpen, setIsGroupInfoOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const textareaRef = useRef(null);

  const name = getChatName(chat, user?.id);
  const avatar = getChatAvatar(chat, user?.id);
  const online = isUserOnline(chat, user?.id);
  
  const otherParticipant = !chat.is_group 
    ? chat.participants?.find(p => p.id !== user?.id) 
    : null;

  // Helper function to scroll to bottom
  const scrollToBottom = useCallback((immediate = false) => {
    // Method 1: Try scrollIntoView on the end marker
    messagesEndRef.current?.scrollIntoView({ 
      behavior: immediate ? 'auto' : 'smooth',
      block: 'end'
    });
    
    // Method 2: Find the actual scroll viewport and scroll it
    const scrollViewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (scrollViewport) {
      scrollViewport.scrollTop = scrollViewport.scrollHeight;
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom(false);
  }, [messages, scrollToBottom]);

  // Force scroll to bottom when chat changes (mobile fix)
  useEffect(() => {
    // Multiple attempts to ensure scroll works on mobile after content loads
    scrollToBottom(true);
    const timer1 = setTimeout(() => scrollToBottom(true), 50);
    const timer2 = setTimeout(() => scrollToBottom(true), 150);
    const timer3 = setTimeout(() => scrollToBottom(true), 300);
    const timer4 = setTimeout(() => scrollToBottom(true), 500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [chat?.id, scrollToBottom]);

  // Also scroll when messages array length changes significantly (initial load)
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(true), 100);
    }
  }, [messages.length, scrollToBottom]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputValue]);

  const handleTyping = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      wsService.sendTyping(chat.id, true);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      wsService.sendTyping(chat.id, false);
    }, 2000);
  }, [chat.id, isTyping]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text) return;

    onSendMessage(text, 'text', null, replyTo?.id);
    setInputValue('');
    setReplyTo(null);
    setIsTyping(false);
    wsService.sendTyping(chat.id, false);
    
    if (textareaRef.current) {
       textareaRef.current.style.height = 'auto';
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Datei zu groß (max 50MB)');
      return;
    }

    setUploadingFile(true);

    try {
      const response = await uploadAPI.upload(file);
      const { url, type, original_name } = response.data;
      onSendMessage(url, type, original_name, replyTo?.id);
      setReplyTo(null);
      toast.success('Datei gesendet');
    } catch (error) {
      console.error("File upload error:", error);
      toast.error('Datei konnte nicht hochgeladen werden');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleVoiceSend = async (audioBlob) => {
    try {
      // Da wir 'File' oben nicht mehr importieren, greift er jetzt auf 
      // das native window.File zu. Das behebt den Fehler!
      const file = new File([audioBlob], `voice_${Date.now()}.webm`, { 
        type: 'audio/webm' 
      });

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { url, original_name } = response.data;
      
      onSendMessage(url, 'voice', original_name, replyTo?.id);
      setReplyTo(null);
      toast.success('Sprachnachricht gesendet');
    } catch (error) {
      console.error('Voice upload error:', error);
      toast.error('Sprachnachricht konnte nicht gesendet werden');
    } finally {
      setIsRecordingVoice(false);
    }
  };

  const handleReply = (message) => {
    setReplyTo(message);
  };

  const handleReact = async (messageId, emoji) => {
    try {
      await api.post(`/messages/${messageId}/react`, { emoji });
    } catch (error) {
      toast.error('Reaktion fehlgeschlagen');
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setEditText(message.content);
  };

  const handleSaveEdit = async () => {
    if (!editText.trim()) return;
    try {
      await api.put(`/messages/${editingMessage.id}`, { content: editText.trim() });
      toast.success('Nachricht bearbeitet');
      setEditingMessage(null);
      setEditText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bearbeiten fehlgeschlagen');
    }
  };

  const handleDelete = async (messageId, forEveryone) => {
    try {
      await api.delete(`/messages/${messageId}?for_everyone=${forEveryone}`);
      toast.success(forEveryone ? 'Nachricht für alle gelöscht' : 'Nachricht für dich gelöscht');
      if (onMessagesUpdate) onMessagesUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const handleForward = (message) => {
    setForwardMessage(message);
  };

  const handleForwardConfirm = async () => {
    if (!forwardMessage || !forwardingTo) return;
    try {
      await messageAPI.forwardMessage(forwardMessage.id, forwardingTo);
      toast.success('Nachricht weitergeleitet');
      setForwardMessage(null);
      setForwardingTo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Weiterleiten fehlgeschlagen');
    }
  };

  const handleClearChat = async () => {
    if (!confirm('Chat-Inhalt für dich löschen?')) return;
    try {
      await api.delete(`/chats/${chat.id}/clear`);
      toast.success('Chat geleert');
      if (onMessagesUpdate) onMessagesUpdate();
    } catch (error) {
      toast.error('Fehler beim Leeren');
    }
  };

  const handleDeleteChat = async () => {
    if (!confirm('Chat für dich löschen?')) return;
    try {
      await api.delete(`/chats/${chat.id}`);
      toast.success('Chat gelöscht');
      onBack();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getTypingUserName = () => {
    if (!typingUserId) return null;
    const typingUser = chat.participants?.find(p => p.id === typingUserId);
    return typingUser?.username;
  };

  const typingUserName = getTypingUserName();
  const forwardableChats = (allChats || []).filter(c => c.id !== chat.id);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        {isMobile && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onBack}
            className="rounded-xl"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}

        <div 
           className={`relative ${chat.is_group ? 'cursor-pointer' : ''}`}
           onClick={() => chat.is_group && setIsGroupInfoOpen(true)}
        >
            <Avatar className="h-10 w-10">
            <AvatarImage src={avatar} />
            <AvatarFallback className="bg-slate-200 dark:bg-slate-700">
                {getInitials(name)}
            </AvatarFallback>
            </Avatar>
        </div>

        <div 
            className={`flex-1 min-w-0 ${chat.is_group ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            onClick={() => chat.is_group && setIsGroupInfoOpen(true)}
        >
          <h2 className="font-semibold text-slate-900 dark:text-white truncate">
            {name}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {typingUserName ? (
              <span className="text-emerald-500">{typingUserName} schreibt...</span>
            ) : online ? (
              <span className="text-emerald-500">Online</span>
            ) : chat.is_group ? (
              `${chat.participants?.length || 0} Teilnehmer`
            ) : (
              'Offline'
            )}
          </p>
        </div>

        <div className="flex items-center gap-1">
          {!chat.is_group && otherParticipant && (
            <>
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                onClick={() => onStartCall && onStartCall('audio', otherParticipant)}
                data-testid="audio-call-btn"
              >
                <Phone className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                onClick={() => onStartCall && onStartCall('video', otherParticipant)}
                data-testid="video-call-btn"
              >
                <Video className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-xl">
                <MoreVertical className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {chat.is_group && (
                <DropdownMenuItem onClick={() => setIsGroupInfoOpen(true)}>
                  Info anzeigen
                </DropdownMenuItem>
              )}
              <DropdownMenuItem>Medien</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleClearChat}>
                Chat leeren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDeleteChat} className="text-red-500">
                Chat löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 chat-bg" ref={scrollAreaRef}>
        <div className="pt-14 pb-4 px-4 space-y-2">
          <AnimatePresence>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={message.sender_id === user?.id}
                showAvatar={chat.is_group && message.sender_id !== user?.id}
                participantCount={chat.participants?.length || 0}
                onReply={handleReply}
                onReact={handleReact}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onForward={handleForward}
                currentUserId={user?.id}
              />
            ))}
          </AnimatePresence>

          {typingUserName && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 px-4"
            >
              <div className="chat-bubble-incoming px-4 py-2">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </motion.div>
          )}

          {uploadingFile && (
            <div className="flex justify-end px-4">
              <div className="chat-bubble-outgoing px-4 py-3 flex items-center gap-2">
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                <span>Hochladen...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Reply Preview */}
      <AnimatePresence>
        {replyTo && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Reply className="w-4 h-4 text-emerald-500" />
                <div className="text-sm">
                  <span className="font-medium text-emerald-600 dark:text-emerald-400">
                    {replyTo.sender_username}
                  </span>
                  <p className="text-slate-500 truncate max-w-[200px]">
                    {replyTo.type !== 'text' ? `📎 ${replyTo.type}` : replyTo.content}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setReplyTo(null)} className="h-6 w-6">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input */}
      <div className="p-1 pb-2 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 relative" style={{ zIndex: 9999 }}>
        <AnimatePresence mode="wait">
          {isRecordingVoice ? (
            <VoiceRecorder
              key="voice-recorder"
              onSend={handleVoiceSend}
              onCancel={() => setIsRecordingVoice(false)}
            />
          ) : (
            <motion.form 
              key="text-input"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={handleSubmit} 
              className="flex items-end gap-1 relative" 
              style={{ zIndex: 9999 }}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rounded-xl shrink-0 h-10 w-10 mb-[1px]" 
                onClick={() => fileInputRef.current?.click()}
                data-testid="attach-file-btn"
              >
                <Paperclip className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </Button>

              <div className="flex-1 relative min-h-[40px] min-w-0" style={{ zIndex: 99999 }}>
                <textarea
                  ref={textareaRef}
                  placeholder="Nachricht..."
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    handleTyping();
                  }}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  className="w-full bg-slate-100 dark:bg-slate-800 border-transparent px-3 py-2 rounded-[20px] resize-none focus:outline-none min-h-[40px] max-h-[120px] overflow-y-auto break-words"
                  style={{ zIndex: 99999 }}
                  data-testid="message-input"
                />
              </div>

              {inputValue.trim() ? (
                <Button
                  type="submit"
                  size="icon"
                  className="h-10 w-10 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shrink-0 relative mb-[1px]"
                  style={{ zIndex: 99999 }}
                  data-testid="send-message-btn"
                >
                  <Send className="w-5 h-5" />
                </Button>
              ) : (
                <Button
                  type="button"
                  size="icon"
                  onClick={() => setIsRecordingVoice(true)}
                  className="h-10 w-10 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shrink-0 relative mb-[1px]"
                  style={{ zIndex: 99999 }}
                  data-testid="voice-message-btn"
                >
                  <Mic className="w-5 h-5" />
                </Button>
              )}
            </motion.form>
          )}
        </AnimatePresence>
      </div>
      
      {/* Edit Dialog */}
      <Dialog open={!!editingMessage} onOpenChange={() => setEditingMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nachricht bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full bg-slate-100 dark:bg-slate-800 border-transparent px-3 py-2 rounded-md resize-none focus:outline-none min-h-[100px] break-words"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMessage(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveEdit}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward Dialog */}
      <Dialog open={!!forwardMessage} onOpenChange={() => { setForwardMessage(null); setForwardingTo(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Forward className="w-5 h-5" />
              Nachricht weiterleiten
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {forwardMessage && (
              <div className="mb-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">
                  Von {forwardMessage.sender_username}
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300 break-all whitespace-normal">
  {forwardMessage.type !== 'text' 
    ? `📎 ${forwardMessage.type === 'image' ? 'Bild' : forwardMessage.type === 'video' ? 'Video' : forwardMessage.type === 'voice' ? 'Sprachnachricht' : 'Datei'}`
    : truncateText(forwardMessage.content, 60)}
</p>
              </div>
            )}
            <p className="text-sm text-slate-500 mb-2">Ziel-Chat auswählen:</p>
            <ScrollArea className="h-[250px]">
              <div className="space-y-1">
                {forwardableChats.length === 0 ? (
                  <p className="text-center text-slate-400 py-4">
                    Keine anderen Chats verfügbar
                  </p>
                ) : (
                  forwardableChats.map((c) => {
                    const chatName = getChatName(c, user?.id);
                    const chatAvatar = getChatAvatar(c, user?.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => setForwardingTo(c.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                          forwardingTo === c.id 
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 border-2 border-emerald-500' 
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={chatAvatar} />
                          <AvatarFallback className="bg-slate-200 dark:bg-slate-700">
                            {getInitials(chatName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-slate-900 dark:text-white">
                            {chatName}
                          </p>
                          {c.is_group && (
                            <p className="text-xs text-slate-500">
                              {c.participants?.length} Teilnehmer
                            </p>
                          )}
                        </div>
                        {forwardingTo === c.id && (
                          <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setForwardMessage(null); setForwardingTo(null); }}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleForwardConfirm}
              disabled={!forwardingTo}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Forward className="w-4 h-4 mr-2" />
              Weiterleiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Info Modal */}
      {chat.is_group && (
        <GroupInfoModal 
          chat={chat}
          isOpen={isGroupInfoOpen}
          onClose={() => setIsGroupInfoOpen(false)}
          onUpdate={onChatUpdate} 
          onLeave={() => {
             setIsGroupInfoOpen(false);
             onBack(); 
          }}
        />
      )}
    </div>
  );
};

export default ChatView;